import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { DisplayItemKey } from '../../../../types/models/Presc'
import { isPublicPath } from '../../../../constants/publicPaths'
import { useAuthCheckQuery } from '../queries/useAuthCheckQuery'

type User = {
  id: string
  pharmacy_name: string
  plan: string
  qr_size: string
  edit_mode: EditMode
  display_items: DisplayItemDict
  app_type: string
  is_annotator: boolean
  is_activated_photo: boolean
  userId: string
  is_image_rotatable: boolean
  editable_item_font_size: string
  show_save_csv_button: boolean
  is_name_able_to_copy: boolean
  is_headquarters: boolean
  use_legacy_ui: boolean
  guide_variant: string
}

type DisplayItemDict = {
  patient_matching: DisplayItemKey[]
  non_matching_main: DisplayItemKey[]
  non_matching_sub: DisplayItemKey[]
  non_matching_other: DisplayItemKey[]
}

type EditMode = {
  kana: boolean
  insurance_num: boolean
  birthday: boolean
  allowed_insurance_num_blank: boolean
}

/**
 * ログインリダイレクト hook（TanStack Query版）
 *
 * 初回レンダリング時に認証チェックを行い、
 * 成功時は localStorage に設定を保存、失敗時はログインページへリダイレクトする。
 *
 * PUBLIC_PATHS（未認証で閲覧するページ）では認証チェック自体を行わない。
 */
export const useRedirectToLogin = (): void => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const dispatch = useDispatch()
  const hasProcessed = useRef(false)
  const isPublic = isPublicPath(pathname)

  const { data, error, isSuccess, isError } = useAuthCheckQuery({
    enabled: !isPublic,
  })

  // 認証成功時の副作用
  useEffect(() => {
    if (isSuccess && data && !hasProcessed.current) {
      hasProcessed.current = true
      const user = data.data.user as User
      console.log(data)

      // Redux dispatch
      dispatch({
        type: 'setIsLoggedIn',
        isLoggedIn: true,
        appType: user.app_type,
      })

      // localStorage への保存
      localStorageWrapper.setItem('qr_size', user.qr_size)
      localStorageWrapper.setItem('userId', user.id)
      localStorageWrapper.setItem(
        'patientMatchingDisplayItems',
        user.display_items.patient_matching
      )
      localStorageWrapper.setItem(
        'nonMatchingMainDisplayItems',
        user.display_items.non_matching_main
      )
      localStorageWrapper.setItem(
        'nonMatchingSubDisplayItems',
        user.display_items.non_matching_sub
      )
      localStorageWrapper.setItem(
        'nonMatchingOtherDisplayItems',
        user.display_items.non_matching_other
      )
      localStorageWrapper.setItem('isPatientKanaEditable', user.edit_mode.kana)
      localStorageWrapper.setItem(
        'isInsuranceNumEditable',
        user.edit_mode.insurance_num
      )
      localStorageWrapper.setItem('isBirthdayEditable', user.edit_mode.birthday)
      localStorageWrapper.setItem(
        'editableItemFontSize',
        user.editable_item_font_size
      )
      localStorageWrapper.setItem(
        'allowedInsuranceNumBlank',
        user.edit_mode.allowed_insurance_num_blank
      )
      localStorageWrapper.setItem('isImageRotatable', user.is_image_rotatable)
      localStorageWrapper.setItem('app_type', user.app_type)
      // アプリ内から開くガイドを版へ振り分けるため（constants/guidePaths.ts）
      localStorageWrapper.setItem('guide_variant', user.guide_variant)
      localStorageWrapper.setItem('is_annotator', user.is_annotator)
      localStorageWrapper.setItem('is_activated_photo', user.is_activated_photo)
      localStorageWrapper.setItem(
        'show_save_csv_button',
        user.show_save_csv_button
      )
      localStorageWrapper.setItem(
        'is_name_able_to_copy',
        user.is_name_able_to_copy
      )
      localStorageWrapper.setItem('is_headquarters', user.is_headquarters)
      // UI 切替フラグはセッション復帰でも同期する。isLegacyUi() は localStorage しか
      // 見ないため、ここで書かないと本 hook が走る通常のページ表示では値が更新されず、
      // ログインフォームを再送信するまでサーバー側の変更が client に届かない。
      localStorageWrapper.setItem('use_legacy_ui', user.use_legacy_ui)
    }
  }, [isSuccess, data, dispatch])

  // 認証失敗時のリダイレクト
  useEffect(() => {
    // enabled:false でも他画面で発生した error はキャッシュから返り続けるため、
    // 公開ページではリダイレクト判定でも抑止する（useShowMaintenanceModal と同型）
    if (isPublic) return
    if (isError && error) {
      console.error(error)
      // query(QuickSync の知らせが指す `?event=`)はログインの先まで持ち越す(login.tsx が `/` に戻すとき付け直す)
      navigate({ pathname: '/login', search })
    }
  }, [isPublic, isError, error, navigate, search])
}
